<template>
  <main class="blog-article-view">
    <section class="blog-article-section">
      <div class="blog-article-section__inner">
        <LoaderView v-if="loading" text="Chargement de l'article..." />

        <div v-else-if="notFound" class="blog-article-empty">
          L'article demandé est introuvable ou n'est pas publié.
        </div>

        <div v-else-if="article" class="blog-article-layout">
          <article class="blog-article-card">
            <img
              v-if="article.featured_image"
              :src="mediaSrc(article.featured_image)"
              :alt="article.title"
              class="blog-article-card__image"
            />

            <div class="blog-article-card__body">
              <p class="blog-article-card__category">{{ article.category.title }}</p>
              <h1 class="blog-article-card__title">{{ article.title }}</h1>

              <div class="blog-article-card__meta">
                <span v-if="article.author_name">{{ article.author_name }}</span>
                <span>{{ formatDate(article.published_at) }}</span>
              </div>

              <p v-if="article.excerpt" class="blog-article-card__excerpt">{{ article.excerpt }}</p>

              <div class="blog-article-card__sharing">
                <span class="blog-article-card__sharing-label">Partager :</span>
                <a
                  :href="facebookShareUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="blog-article-card__sharing-link blog-article-card__sharing-link--facebook"
                  aria-label="Partager sur Facebook"
                >
                  <font-awesome-icon :icon="['fab', 'facebook-f']" />
                </a>
                <a
                  :href="linkedinShareUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="blog-article-card__sharing-link blog-article-card__sharing-link--linkedin"
                  aria-label="Partager sur LinkedIn"
                >
                  <font-awesome-icon :icon="['fab', 'linkedin-in']" />
                </a>
                <button
                  type="button"
                  class="blog-article-card__sharing-link blog-article-card__sharing-link--instagram"
                  aria-label="Partager sur Instagram"
                  title="Partager sur Instagram"
                  @click="shareOnInstagram"
                >
                  <font-awesome-icon :icon="['fab', 'instagram']" />
                </button>
              </div>
              <p v-if="actionMessage" class="blog-article-card__sharing-feedback">{{ actionMessage }}</p>

              <div class="blog-article-card__content" v-html="articleContent"></div>
            </div>
          </article>

          <BlogSidebar
            :categories="categories"
            :recent-articles="recentArticles"
            :search-value="searchQuery"
            :current-search-query="searchQuery"
            @search="handleSearch"
          />
        </div>
      </div>
    </section>
  </main>
</template>

<script>
import { api } from "@/api";
import BlogSidebar from "@/components/BlogSidebar.vue";
import LoaderView from "@/components/LoaderView.vue";

export default {
  name: "BlogArticleView",
  components: {
    BlogSidebar,
    LoaderView,
  },
  data() {
    return {
      loading: true,
      notFound: false,
      article: null,
      categories: [],
      recentArticles: [],
      actionMessage: "",
      actionMessageTimer: null,
    };
  },
  computed: {
    articleSlug() {
      return this.$route.params.articleSlug;
    },
    searchQuery() {
      return "";
    },
    publicUrl() {
      return window.location.href;
    },
    facebookShareUrl() {
      return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(this.publicUrl)}`;
    },
    linkedinShareUrl() {
      return `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(this.publicUrl)}`;
    },
    articleContent() {
      const content = this.article && this.article.content;
      if (!content) return "";
      const baseUrl = process.env.VUE_APP_BASE_URL || "";
      // Réécrit les URLs relatives /media/... (images uploadées via CKEditor)
      // pour pointer vers le backend plutôt que le frontend.
      return content.replace(/(src|href)="\/media\//g, `$1="${baseUrl}/media/`);
    },
  },
  watch: {
    articleSlug: {
      immediate: true,
      handler() {
        this.loadPage();
      },
    },
  },
  beforeDestroy() {
    if (this.actionMessageTimer) {
      clearTimeout(this.actionMessageTimer);
    }
  },
  methods: {
    loadPage() {
      this.loading = true;
      this.notFound = false;

      Promise.all([this.loadArticle(), this.loadSidebar()])
        .catch(() => {})
        .finally(() => {
          this.loading = false;
          this.updateHead();
        });
    },
    loadArticle() {
      return api
        .get(`/blog/articles/${this.articleSlug}/`)
        .then((response) => {
          this.article = response.data || null;
        })
        .catch((error) => {
          if (error.response && error.response.status === 404) {
            this.notFound = true;
            this.article = null;
            return;
          }

          throw error;
        });
    },
    loadSidebar() {
      return Promise.all([
        api.get("/blog/categories/"),
        api.get("/blog/articles/recent/"),
      ]).then(([categoriesResponse, recentResponse]) => {
        this.categories = Array.isArray(categoriesResponse.data) ? categoriesResponse.data : [];
        this.recentArticles = Array.isArray(recentResponse.data) ? recentResponse.data : [];
      });
    },
    handleSearch(query) {
      this.$router.push({
        name: "blog-list",
        query: query ? { q: query } : {},
      });
    },
    updateHead() {
      this.removeJsonLd();

      if (!this.article) {
        document.title = "Article introuvable | Blog Mes Meilleurs Menus";
        this.setMetaTag("description", "L'article demandé est introuvable.");
        this.setCanonicalLink(`${window.location.origin}${this.$route.fullPath}`);
        return;
      }

      const title = this.article.meta_title || this.article.title;
      const description = this.article.meta_description || this.article.excerpt || "";
      const canonicalUrl = `${window.location.origin}${this.$route.fullPath}`;
      const image = this.article.featured_image
        ? this.mediaSrc(this.article.featured_image)
        : `${window.location.origin}/favicon.png`;

      document.title = `${title} | Blog Mes Meilleurs Menus`;
      this.setMetaTag("description", description);
      this.setMetaTag("og:title", title, true);
      this.setMetaTag("og:description", description, true);
      this.setMetaTag("og:type", "article", true);
      this.setMetaTag("og:url", canonicalUrl, true);
      this.setMetaTag("og:image", image, true);
      this.setMetaTag("twitter:image", image);
      this.setMetaTag("twitter:card", "summary_large_image");
      this.setMetaTag("twitter:title", title);
      this.setMetaTag("twitter:description", description);
      this.setCanonicalLink(canonicalUrl);
      this.setJsonLd(canonicalUrl, image);
    },
    setMetaTag(name, content, isProperty = false) {
      const selector = isProperty ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let tag = document.head.querySelector(selector);

      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute(isProperty ? "property" : "name", name);
        document.head.appendChild(tag);
      }

      tag.setAttribute("content", content);
    },
    setCanonicalLink(url) {
      let tag = document.head.querySelector('link[rel="canonical"]');

      if (!tag) {
        tag = document.createElement("link");
        tag.setAttribute("rel", "canonical");
        document.head.appendChild(tag);
      }

      tag.setAttribute("href", url);
    },
    setJsonLd(canonicalUrl, image) {
      this.removeJsonLd();

      if (!this.article) {
        return;
      }

      const jsonLd = {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        headline: this.article.title,
        description: this.article.meta_description || this.article.excerpt || "",
        url: canonicalUrl,
        datePublished: this.article.published_at,
        dateModified: this.article.published_at,
        articleSection: this.article.category.title,
        author: this.article.author_name
          ? {
            "@type": "Person",
            name: this.article.author_name,
          }
          : undefined,
        image: image ? [image] : undefined,
      };

      const script = document.createElement("script");
      script.id = "blog-json-ld";
      script.type = "application/ld+json";
      script.text = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    },
    removeJsonLd() {
      const existing = document.head.querySelector("#blog-json-ld");
      if (existing) {
        existing.remove();
      }
    },
    formatDate(value) {
      if (!value) {
        return "";
      }

      return new Intl.DateTimeFormat("fr-FR", {
        day: "numeric",
        month: "long",
        year: "numeric",
      }).format(new Date(value));
    },
    mediaSrc(uri) {
      if (!uri) return null;
      if (uri.startsWith("http")) return uri;
      return `${process.env.VUE_APP_BASE_URL}${uri}`;
    },
    async copyText(text) {
      if (typeof navigator !== "undefined" && navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        return;
      }

      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.setAttribute("readonly", "readonly");
      textarea.style.position = "absolute";
      textarea.style.left = "-9999px";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    },
    showActionMessage(message) {
      this.actionMessage = message;

      if (this.actionMessageTimer) {
        clearTimeout(this.actionMessageTimer);
      }

      this.actionMessageTimer = setTimeout(() => {
        this.actionMessage = "";
      }, 3200);
    },
    async shareOnInstagram() {
      try {
        await this.copyText(this.publicUrl);
        this.showActionMessage("Le lien de l’article a été copié pour le partager sur Instagram.");
      } catch (error) {
        this.showActionMessage("Impossible de copier le lien automatiquement.");
      }
    },
  },
};
</script>

<style scoped>
.blog-article-view {
  padding-top: 110px;
  padding-bottom: 48px;
}

.blog-article-section {
  padding: 40px 20px 0;
}

.blog-article-section__inner {
  max-width: 1320px;
  margin: 0 auto;
}

.blog-article-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: 32px;
  align-items: start;
}

.blog-article-card {
  overflow: hidden;
  border-radius: 24px;
  background: #fff;
  box-shadow: 0 12px 34px rgba(0, 76, 64, 0.08);
}

.blog-article-card__image {
  display: block;
  width: 100%;
  max-height: 460px;
  object-fit: cover;
}

.blog-article-card__body {
  padding: 32px;
}

.blog-article-card__category {
  margin: 0 0 12px;
  color: #e57b57;
  font-size: 14px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.blog-article-card__title {
  margin: 0;
  color: #004c40;
  font-family: "Amanise";
  font-size: clamp(34px, 4vw, 58px);
  line-height: 1.05;
}

.blog-article-card__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 16px;
  margin-top: 18px;
  color: #6a7a74;
  font-size: 14px;
  font-weight: 700;
}

.blog-article-card__excerpt {
  margin: 20px 0 0;
  color: #35524a;
  font-size: 18px;
  line-height: 1.7;
}

.blog-article-card__sharing {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 24px;
}

.blog-article-card__sharing-label {
  color: #004c40;
  font-size: 14px;
  font-weight: 700;
}

.blog-article-card__sharing-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border: 0;
  border-radius: 999px;
  color: #fff;
  cursor: pointer;
  text-decoration: none;
}

.blog-article-card__sharing-link--facebook {
  background: #1877f2;
}

.blog-article-card__sharing-link--linkedin {
  background: #0a66c2;
}

.blog-article-card__sharing-link--instagram {
  background: linear-gradient(135deg, #fdc468, #df4996 55%, #4f5bd5);
}

.blog-article-card__sharing-feedback {
  margin: 12px 0 0;
  color: #004c40;
  font-size: 14px;
  font-weight: 700;
}

.blog-article-card__content {
  margin-top: 28px;
  color: #35524a;
  font-family: "Merriweather", serif;
  font-size: 17px;
  line-height: 1.85;
}

.blog-article-card__content :deep(h2),
.blog-article-card__content :deep(h3) {
  margin-top: 36px;
  margin-bottom: 16px;
  color: #004c40;
  font-family: "TangoSans";
  line-height: 1.3;
}

.blog-article-card__content :deep(p),
.blog-article-card__content :deep(ul),
.blog-article-card__content :deep(ol) {
  margin-top: 0;
  margin-bottom: 18px;
}

.blog-article-card__content :deep(a) {
  color: #004c40;
  font-weight: 700;
}

.blog-article-empty {
  padding: 48px 24px;
  border-radius: 18px;
  background: #f5f1e8;
  color: #35524a;
  text-align: center;
}

@media only screen and (max-width: 1023px) {
  .blog-article-layout {
    grid-template-columns: 1fr;
  }
}

@media only screen and (max-width: 899px) {
  .blog-article-view {
    padding-top: 90px;
  }
}

@media only screen and (max-width: 599px) {
  .blog-article-section {
    padding-left: 16px;
    padding-right: 16px;
  }

  .blog-article-card__body {
    padding: 20px;
  }

  .blog-article-card__content {
    font-size: 16px;
    line-height: 1.75;
  }
}
</style>
